export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface ReminderSchedule {
  days: DayOfWeek[];
  /** Minutes since midnight in the user's local timezone */
  timeLocalMinutesSinceMidnight: number;
}

export interface NotificationCategories {
  practiceReminders: boolean;
  practiceReminderSchedule: ReminderSchedule;
  sessionSummaries: boolean;
  achievements: boolean;
  breathworkAndJourneyUpdates: boolean;
  /** Delivered via email only, regardless of enabled types */
  marketing: boolean;
}

export interface NotificationTypes {
  email: boolean;
  push: boolean;
}

export interface NotificationSettings {
  enabled: boolean;
  categories: NotificationCategories;
  types: NotificationTypes;
}

export type BreathworkNameDisplay = 'english' | 'sanskrit';

export interface AudioSettings {
  isMusicOn: boolean;
  isGuidingVoiceOn: boolean;
  isCountdownOn: boolean;
  isBreathingSoundOn: boolean;
}

export interface UserSettings {
  notifications: NotificationSettings;
  breathworkNameDisplay: BreathworkNameDisplay;
  audio: AudioSettings;
  isSkipTutorialOn: boolean;
  /** Mobile: false stops Firebase Analytics collection. Absent/true = enabled
   *  (legitimate-interest default). The web app gates analytics via the cookie
   *  consent banner instead and ignores this field. */
  isAnalyticsOn?: boolean;
  /** iOS: tracks whether the system push-permission dialog has been shown.
   *  Once denied, iOS never re-shows the dialog — use this flag to avoid
   *  re-prompting and instead direct the user to system Settings. */
  hasAskedForNotifications?: boolean;
}

export function defaultUserSettings(): UserSettings {
  return {
    breathworkNameDisplay: 'sanskrit',
    isSkipTutorialOn: false,
    isAnalyticsOn: true,
    audio: {
      isMusicOn: true,
      isGuidingVoiceOn: true,
      isCountdownOn: true,
      isBreathingSoundOn: true,
    },
    notifications: {
      enabled: false,
      categories: {
        practiceReminders: false,
        practiceReminderSchedule: {
          days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
          timeLocalMinutesSinceMidnight: 540, // 09:00 local
        },
        sessionSummaries: true,
        achievements: true,
        breathworkAndJourneyUpdates: false,
        marketing: false,
      },
      types: {
        email: true,
        push: false,
      },
    },
  };
}

export function userSettingsFromJson(json: Record<string, unknown>): UserSettings {
  const defaults = defaultUserSettings();
  const n = (json['notifications'] as Record<string, unknown>) ?? {};
  const cat = (n['categories'] as Record<string, unknown>) ?? {};
  const types = (n['types'] as Record<string, unknown>) ?? {};

  return {
    breathworkNameDisplay: (json['breathworkNameDisplay'] as BreathworkNameDisplay | undefined) ?? defaults.breathworkNameDisplay,
    isSkipTutorialOn: (json['isSkipTutorialOn'] as boolean | undefined) ?? defaults.isSkipTutorialOn,
    isAnalyticsOn: (json['isAnalyticsOn'] as boolean | undefined) ?? defaults.isAnalyticsOn,
    audio: (() => {
      const a = (json['audio'] as Record<string, unknown>) ?? {};
      return {
        isMusicOn: (a['isMusicOn'] as boolean) ?? defaults.audio.isMusicOn,
        isGuidingVoiceOn: (a['isGuidingVoiceOn'] as boolean) ?? defaults.audio.isGuidingVoiceOn,
        isCountdownOn: (a['isCountdownOn'] as boolean) ?? defaults.audio.isCountdownOn,
        isBreathingSoundOn: (a['isBreathingSoundOn'] as boolean) ?? defaults.audio.isBreathingSoundOn,
      };
    })(),
    notifications: {
      enabled: (n['enabled'] as boolean) ?? defaults.notifications.enabled,
      categories: (() => {
        const rawSchedule = (cat['practiceReminderSchedule'] as Record<string, unknown>) ?? {};
        const defaultSchedule = defaults.notifications.categories.practiceReminderSchedule;
        const ALL_DAYS: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
        const rawDays = rawSchedule['days'];
        const parsedDays = Array.isArray(rawDays)
          ? (rawDays as string[]).filter((d): d is DayOfWeek => ALL_DAYS.includes(d as DayOfWeek))
          : defaultSchedule.days;
        return {
          practiceReminders: (cat['practiceReminders'] as boolean) ?? defaults.notifications.categories.practiceReminders,
          practiceReminderSchedule: {
            days: parsedDays.length > 0 ? parsedDays : defaultSchedule.days,
            timeLocalMinutesSinceMidnight: (rawSchedule['timeLocalMinutesSinceMidnight'] as number) ?? defaultSchedule.timeLocalMinutesSinceMidnight,
          },
          sessionSummaries: (cat['sessionSummaries'] as boolean) ?? defaults.notifications.categories.sessionSummaries,
          achievements: (cat['achievements'] as boolean) ?? defaults.notifications.categories.achievements,
          breathworkAndJourneyUpdates: (cat['breathworkAndJourneyUpdates'] as boolean) ?? defaults.notifications.categories.breathworkAndJourneyUpdates,
          marketing: (cat['marketing'] as boolean) ?? defaults.notifications.categories.marketing,
        };
      })(),
      types: {
        email: (types['email'] as boolean) ?? defaults.notifications.types.email,
        push: (types['push'] as boolean) ?? defaults.notifications.types.push,
      },
    },
  };
}
